import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X, Settings, Cookie } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = "gogenia_cookie_consent";

const CookieConsent = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const content = {
    fr: {
      description: "Ce site utilise des cookies pour améliorer votre expérience.",
      acceptAll: "Accepter",
      rejectAll: "Refuser",
      customize: "Options",
      save: "Enregistrer",
      preferencesTitle: "Préférences cookies",
      preferencesDescription: "Gérez vos préférences de cookies ci-dessous.",
      cookies: {
        necessary: {
          title: "Cookies nécessaires",
          description: "Essentiels au fonctionnement du site."
        },
        analytics: {
          title: "Cookies analytiques",
          description: "Nous aident à comprendre l'utilisation du site."
        },
        marketing: {
          title: "Cookies marketing",
          description: "Permettent des contenus personnalisés."
        }
      },
      privacyLink: "En savoir plus"
    },
    en: {
      description: "This site uses cookies to improve your experience.",
      acceptAll: "Accept",
      rejectAll: "Reject",
      customize: "Options",
      save: "Save",
      preferencesTitle: "Cookie preferences",
      preferencesDescription: "Manage your cookie preferences below.",
      cookies: {
        necessary: {
          title: "Necessary cookies",
          description: "Essential for the website to function."
        },
        analytics: {
          title: "Analytics cookies",
          description: "Help us understand site usage."
        },
        marketing: {
          title: "Marketing cookies",
          description: "Allow personalized content."
        }
      },
      privacyLink: "Learn more"
    }
  };

  const t = content[language];

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay appearance by 4 seconds to not overwhelm user on arrival
      const timer = setTimeout(() => setIsVisible(true), 4000);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch {
        setIsVisible(true);
      }
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleRejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Ultra compact banner - optimized for mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-3 bg-card/98 backdrop-blur-md border-t border-border shadow-lg animate-in slide-in-from-bottom-4 duration-300">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            {/* Text - hidden on very small screens, compact on mobile */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Cookie className="h-4 w-4 text-primary shrink-0 hidden xs:block" />
              <p className="text-xs text-muted-foreground truncate sm:whitespace-normal">
                <span className="hidden sm:inline">{t.description} </span>
                <a href="/legal" className="text-primary hover:underline whitespace-nowrap">
                  {t.privacyLink}
                </a>
              </p>
            </div>
            
            {/* Buttons - always visible, compact */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreferences(true)}
                className="h-7 px-2 text-xs"
              >
                <Settings className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">{t.customize}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                className="h-7 px-2 sm:px-3 text-xs"
              >
                {t.rejectAll}
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="h-7 px-2 sm:px-3 text-xs"
              >
                {t.acceptAll}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences dialog */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-[95vw] sm:max-w-md p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Cookie className="h-4 w-4 text-primary" />
              {t.preferencesTitle}
            </DialogTitle>
            <DialogDescription className="text-xs">{t.preferencesDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            {/* Necessary cookies */}
            <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-muted/50">
              <div className="space-y-0.5 min-w-0">
                <p className="font-medium text-xs">{t.cookies.necessary.title}</p>
                <p className="text-[10px] text-muted-foreground leading-tight">{t.cookies.necessary.description}</p>
              </div>
              <Switch checked disabled className="scale-90" />
            </div>

            {/* Analytics cookies */}
            <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-muted/50">
              <div className="space-y-0.5 min-w-0">
                <p className="font-medium text-xs">{t.cookies.analytics.title}</p>
                <p className="text-[10px] text-muted-foreground leading-tight">{t.cookies.analytics.description}</p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, analytics: checked }))
                }
                className="scale-90"
              />
            </div>

            {/* Marketing cookies */}
            <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-muted/50">
              <div className="space-y-0.5 min-w-0">
                <p className="font-medium text-xs">{t.cookies.marketing.title}</p>
                <p className="text-[10px] text-muted-foreground leading-tight">{t.cookies.marketing.description}</p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, marketing: checked }))
                }
                className="scale-90"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowPreferences(false)} className="h-8 text-xs">
              <X className="h-3 w-3 mr-1" />
              {language === "fr" ? "Fermer" : "Close"}
            </Button>
            <Button size="sm" onClick={handleSavePreferences} className="h-8 text-xs">
              {t.save}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
